import {Button, Callout, Icon, Intent, Tag, Tooltip} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {useObserver} from 'mobx-react-lite';
import React from 'react';
import {useHistory} from 'react-router-dom';
import {addToQuery} from '../../lib/history';
import {titleize} from '../../lib/titleize';
import {showToasts} from '../../lib/toaster';
import {AppDetailStore} from '../../stores/AppDetailStore';
import {directoryStore} from '../../stores/DirectoryStore';
import {SegmentAnalytics} from '../../lib/SegmentAnalytics';
import styles from './AppInfoCard.module.sass';
import {UninstallButton} from './UninstallButton';
import {withCacheBust} from '../../lib/cacheBust';
import {CmsInstanceSelector} from './CmsInstanceSelector/CmsInstanceSelector';

interface Props {
  detailStore: AppDetailStore;
}

export const AppInfoCard = ({detailStore}: Props) => {
  const internalApps = ['b2b_commerce', 'cms', 'commerce_cloud', 'cmp_integration', 'content_intelligence', 'optimizely_campaign'];
  const history = useHistory();

  const onInstall = async () => {
    const {pathname, search} = window.location;
    const wasInstalled = detailStore.installed;
    const success = await detailStore.install();
    SegmentAnalytics.track('App Action', {
      page: new URLSearchParams(search).get('tab') || 'overview',
      path: pathname + search,
      action: 'Install',
      success,
      appId: detailStore.appId,
      appVersion: detailStore.version,
      isInstalled: wasInstalled,
    });
    if (success) {
      addToQuery(history, {tab: 'settings'});
      showToasts([{intent: 'success', message: 'Installed!'}]);
    }
    // The failure toast comes from the store, which has the API's actual error message
  };

  return useObserver(() => {
    const {loading, detail, version, installed, installing} = detailStore;
    if (loading || !detail) {
      return <div className={styles.container} />;
    }

    const {hasCmsInstallationScope, latestRunningVersion} = detailStore;
    const selectedScope = detailStore.selectedCmsScope;
    const hasInstallableVersion = !!latestRunningVersion;
    // A per-instance app additionally needs an instance selected and a running version to
    // install onto; `installed` already means "the selected instance is installed" for those.
    const cannotInstall = directoryStore.requiresAccountUpgrade(detailStore.appId) ||
      (hasCmsInstallationScope && (!selectedScope || !hasInstallableVersion));

    return (
      <div className={styles.container}>
        {hasCmsInstallationScope && directoryStore.trackerId && (
          <CmsInstanceSelector detailStore={detailStore} />
        )}
        <div className={styles.row}>
          <img className={styles.logoMark} src={withCacheBust(detail.logo_url)} alt="App Logo" />
          <div>
            <span className={styles.appName}>{detail.display_name}</span>
            {version && <span className={styles.version}>v{version}</span>}
          </div>
          <div className={styles.vendor}>by {titleize(detail.vendor)}</div>

          <div className={styles.buttons} style={{display: directoryStore.trackerId ? 'flex' : 'none'}}>
            <Tooltip
              content="No stable version available for installation."
              disabled={installed || hasInstallableVersion || latestRunningVersion === undefined}
            >
              <Button
                intent={installed ? Intent.SUCCESS : Intent.PRIMARY}
                disabled={installed || cannotInstall}
                loading={installing}
                onClick={onInstall}
              >{installed ?
                renderInstallButtonContents(detailStore) :
                'Install App'
                }</Button>
            </Tooltip>
            <UninstallButton history={history} detailStore={detailStore} />
          </div>
        </div>
        {directoryStore.requiresAccountUpgrade(detailStore.appId) &&
          <div>
            <div className={styles.row}>
              <Callout intent={Intent.PRIMARY} icon={null}>
                {directoryStore.accountUpgradeMessage()}
                <br/>
                <a href="mailto:CSM.Leaders@optimizely.com">Contact Sales</a>
              </Callout>
            </div>
            <div className={styles.divider} />
          </div>
        }
        <div className={styles.categories}>
          {renderCategories(detailStore)}
        </div>
        <div className={styles.divider}></div>
        <div className={styles.note} style={{display: internalApps.includes(detailStore.appId) ? 'none' : 'block'}}>
          <div>By installing this app you accept <a rel="noopener noreferrer" href="https://www.optimizely.com/legal/third-party-add-ons-platform-integration-terms" target="_blank">Third-Party Add-Ons & Platform Integration Terms</a></div>
        </div>
        <div />
        {renderLinks(detailStore)}
      </div>
    );
  });
};

const renderCategories = (store: AppDetailStore) => (
  store.detail!.categories.map((category) => <Tag key={category}>{category}</Tag>)
);

const renderLinks = (store: AppDetailStore) => (
  <div className={styles.links}>
    <a
      rel="noopener noreferrer"
      href={store.detail!.support_url || 'https://apps.zaius.com/'}
      target="_blank"
    >
      App Help
    </a>
  </div>
);

const renderInstallButtonContents = (store: AppDetailStore) => (
  store.sameVersionInstalled ?
    <span>Installed <Icon icon={IconNames.TICK} className={styles.check} /></span> :
    <span>Installed [{store.installedVersion}]<Icon icon={IconNames.TICK} className={styles.check} /></span>
);
